import { Navigate, Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export const ROLE_LABELS = { owner: "OWNER", reviewer: "REVIEWER" };

export default function AdminLayout() {
  const { user, checked, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (!checked) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <p className="text-sm uppercase tracking-[0.3em] text-muted-foreground animate-pulse">
          Loading
        </p>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/admin" state={{ from: location }} replace />;
  }

  const handleLogout = async () => {
    await logout();
    navigate("/admin", { replace: true });
  };

  const navCls = ({ isActive }) =>
    `text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
      isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
    }`;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header
        data-testid="admin-header"
        className="sticky top-0 z-40 bg-black/60 backdrop-blur-xl border-b border-white/10"
      >
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6 md:gap-10 min-w-0">
            <NavLink to="/admin/applications" className="shrink-0">
              <span className="font-display text-xl font-bold tracking-tight">
                CAN<span className="text-primary">ARTS</span>
              </span>
              <span className="hidden md:block text-[10px] uppercase tracking-[0.3em] text-secondary">
                Artist Discovery
              </span>
            </NavLink>
            <nav className="flex items-center gap-5">
              <NavLink
                to="/admin/applications"
                data-testid="admin-nav-applications"
                className={navCls}
              >
                Applications
              </NavLink>
              {user.role === "owner" && (
                <NavLink
                  to="/admin/settings"
                  data-testid="admin-nav-settings"
                  className={navCls}
                >
                  Settings
                </NavLink>
              )}
            </nav>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div data-testid="admin-user-chip" className="hidden sm:block text-right">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] leading-tight">
                {user.name}
              </p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-secondary leading-tight">
                {ROLE_LABELS[user.role] || user.role}
              </p>
            </div>
            <button
              data-testid="admin-logout"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-primary transition-colors duration-300"
            >
              <LogOut size={14} />
              <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-[1200px] mx-auto px-5 md:px-8 py-8 md:py-10">
        <Outlet />
      </main>
    </div>
  );
}
